import { useQuery, useQueryClient } from "@tanstack/react-query"
import { extractApiErrorMessage } from "@velizhanin-dev/api-errors"
import axios from "axios"
import { History, RotateCcw } from "lucide-react"
import { useState } from "react"
import { toast } from "sonner"
import {
	botBuilderKeys,
	listVersions,
	restoreVersion,
	type ScenarioDraft,
	type ScenarioVersion,
} from "@/entities/bot-builder"
import { Badge } from "@/shared/ui/badge"
import { Button } from "@/shared/ui/button"
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/shared/ui/collapsible"
import { useConfirm } from "@/shared/ui/confirm-provider"
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogHeader,
	DialogTitle,
} from "@/shared/ui/dialog"
import { Skeleton } from "@/shared/ui/skeleton"
import { countDiff, DiffView } from "./diff-view"
import { LoadSwap } from "@/shared/ui/load-swap"

const formatDateTime = (value: string) =>
	new Date(value).toLocaleString("ru-RU", { dateStyle: "short", timeStyle: "short" })

const authorName = (author: ScenarioVersion["publishedBy"]) => {
	if (!author) return "неизвестно"
	const name = [author.firstName, author.lastName].filter(Boolean).join(" ")
	return name || (author.username ? `@${author.username}` : "неизвестно")
}

function VersionRow({
	version,
	draft,
	busy,
	onRestore,
}: {
	version: ScenarioVersion
	draft: ScenarioDraft
	busy: boolean
	onRestore: () => void
}) {
	const [expanded, setExpanded] = useState(false)
	const changes = version.changeSummary ? countDiff(version.changeSummary) : 0
	return (
		<div className="rounded-lg border border-border/60 p-3">
			<div className="flex flex-wrap items-center gap-2">
				<span className="font-medium">Версия {version.number}</span>
				{version.isLatest ? <Badge variant="secondary">Сейчас у ботов</Badge> : null}
				<span className="text-xs text-muted-foreground">
					{formatDateTime(version.publishedAt)} · {authorName(version.publishedBy)}
				</span>
				<Button variant="ghost" size="sm" className="ml-auto" disabled={busy} onClick={onRestore}>
					<RotateCcw />В черновик
				</Button>
			</div>
			{version.comment ? <p className="mt-1 text-sm">{version.comment}</p> : null}
			{version.changeSummary && changes > 0 && version.number > 1 ? (
				<Collapsible open={expanded} onOpenChange={setExpanded} className="mt-1">
					<CollapsibleTrigger asChild>
						<Button
							type="button"
							variant="ghost"
							className="h-auto p-0 text-xs text-muted-foreground hover:bg-transparent hover:text-foreground"
						>
							{expanded ? "Скрыть изменения" : `Изменений: ${changes}`}
						</Button>
					</CollapsibleTrigger>
					<CollapsibleContent>
						<div className="mt-2">
							<DiffView diff={version.changeSummary} draft={draft} />
						</div>
					</CollapsibleContent>
				</Collapsible>
			) : null}
		</div>
	)
}

interface VersionsDialogProps {
	open: boolean
	onOpenChange: (open: boolean) => void
	draft: ScenarioDraft
	isSaving: boolean
}

/**
 * История публикаций. Восстановление заменяет черновик версией целиком и не
 * трогает ботов, они отвечают по опубликованной версии, пока её не перепубликуют.
 */
export function VersionsDialog({ open, onOpenChange, draft, isSaving }: VersionsDialogProps) {
	const queryClient = useQueryClient()
	const { confirm } = useConfirm()
	const [restoring, setRestoring] = useState(false)
	const versions = useQuery({
		queryKey: botBuilderKeys.versions(draft.id),
		queryFn: () => listVersions(draft.id),
		enabled: open,
		staleTime: 0,
	})

	const restore = (version: ScenarioVersion) =>
		confirm({
			title: `Вернуть версию ${version.number} в черновик?`,
			message:
				"Текущий черновик заменится этой версией целиком, неопубликованные правки пропадут. Боты продолжат отвечать по опубликованной версии, пока вы не опубликуете снова.",
			confirmText: "Вернуть",
			onConfirm: async () => {
				setRestoring(true)
				try {
					await restoreVersion(draft.id, version.id, draft.revision)
					await queryClient.invalidateQueries({ queryKey: botBuilderKeys.scenario(draft.id) })
					toast.success(`Версия ${version.number} в черновике`)
					onOpenChange(false)
				} catch (error) {
					if (axios.isAxiosError(error) && error.response?.status === 409) {
						toast.warning("Черновик изменили в другом окне. Загрузили свежую версию")
						await queryClient.invalidateQueries({ queryKey: botBuilderKeys.scenario(draft.id) })
					} else {
						toast.error(extractApiErrorMessage(error, "Не удалось вернуть версию"))
					}
				} finally {
					setRestoring(false)
				}
			},
		})

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-xl">
				<DialogHeader>
					<DialogTitle className="flex items-center gap-2">
						<History className="h-5 w-5" />
						История версий
					</DialogTitle>
					<DialogDescription>
						Боты отвечают по версии с отметкой «Сейчас у ботов».
					</DialogDescription>
				</DialogHeader>
				<LoadSwap loading={versions.isLoading}>
					{versions.isLoading ? (
					<div className="space-y-2">
						<Skeleton className="h-16 w-full" />
						<Skeleton className="h-16 w-full" />
					</div>
				) : versions.data && versions.data.length > 0 ? (
					<div className="space-y-2">
						{versions.data.map((version) => (
							<VersionRow
								key={version.id}
								version={version}
								draft={draft}
								busy={restoring || isSaving}
								onRestore={() => restore(version)}
							/>
						))}
					</div>
				) : versions.isError ? (
					<p className="text-sm text-red-400">
						{extractApiErrorMessage(versions.error, "Не удалось загрузить историю")}
					</p>
				) : (
					<p className="text-sm text-muted-foreground">Сценарий ещё не публиковали.</p>
				)}
				</LoadSwap>
			</DialogContent>
		</Dialog>
	)
}
